import React from 'react'
import { css } from '@emotion/react'
import { colors, mq } from 'plume-ui'
import { Article, Report, Seminar } from '../../../types'
import { TagButton, TagButtonGroup } from '../../atoms/TagButton'
import { desktopMinWidth } from '../../tokens/mediaquery'
import { TitledPublishedDate } from './ArticlePublishedDate'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from '../../atoms/Breadcrumb'
import { ContentfulResponsiveImage } from './ContentfulResponsiveImage'
import { LockIcon } from './LockIcon'

type Props = {
  content: Article | Report | Seminar
  previewText?: string
  breadcrumbs: {
    title: string
    url?: string
    external?: boolean
  }[]
  className?: string
}

export const Eyecatch: React.FC<Props> = (props) => {
  return (
    <div
      css={css`
        position: relative;
      `}
      className={props.className}
    >
      {props.previewText && <PreviewLabel>{props.previewText}</PreviewLabel>}
      <div>
        <div
          css={css`
            display: none;
            ${mq.large} {
              display: block;
              max-width: 1120px;
              margin: 16px auto 32px;
            }
          `}
        >
          <div
            css={css`
              margin: 0 24px;
            `}
          >
            <Breadcrumb>
              {props.breadcrumbs.map((breadcrumb, i) =>
                breadcrumb.url ? (
                  <BreadcrumbLink
                    href={breadcrumb.url}
                    external={breadcrumb.external}
                    key={i}
                  >
                    {breadcrumb.title}
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbItem key={i}>{breadcrumb.title}</BreadcrumbItem>
                )
              )}
            </Breadcrumb>
          </div>
        </div>
        <div
          css={css`
            @media (min-width: ${desktopMinWidth}) {
              padding-top: 24px;
            }
          `}
        >
          <div
            css={css`
              @media (min-width: ${desktopMinWidth}) {
                margin: 0 auto;
                width: 730px;
              }
            `}
          >
            <TitledPublishedDate
              publishedAt={props.content.publishedAt}
            ></TitledPublishedDate>
            <h1
              css={css`
                font-size: 20px;
                line-height: 1.5;
                margin: 15px 0 20px;
                padding: 0 20px;
                color: ${colors.black10};
                @media (min-width: ${desktopMinWidth}) {
                  font-size: 28px;
                  margin: 12px 3px;
                  padding: 0;
                }
              `}
            >
              {isArticle(props.content) && (
                <LockIcon size="small" article={props.content} />
              )}
              {props.content.title}
            </h1>
            {isArticle(props.content) && props.content.newTags && (
              <>
                <div
                  css={css`
                    margin-bottom: 15px;
                    padding: 0 20px;
                    ${mq.large} {
                      display: none;
                    }
                  `}
                >
                  <TagButtonGroup>
                    {props.content.newTags.map((tag, i) => (
                      <TagButton tag={tag} key={i} />
                    ))}
                  </TagButtonGroup>
                </div>
                <div
                  css={css`
                    display: none;
                    ${mq.large} {
                      display: block;
                      margin-bottom: 20px;
                    }
                  `}
                >
                  <TagButtonGroup>
                    {props.content.newTags.map((tag, i) => (
                      <TagButton tag={tag} key={i} />
                    ))}
                  </TagButtonGroup>
                </div>
              </>
            )}
          </div>
          <ContentfulResponsiveImage
            src={props.content.headerImage.url}
            css={css`
              width: 100vw;
              object-fit: cover;
              object-position: center;

              @media (min-width: ${desktopMinWidth}) {
                max-height: 100%;
                width: 829px;
                height: 434px;
                display: block;
                margin: 0 auto;
                -webkit-border-radius: 5px;
                border-radius: 5px;
              }
            `}
          />
        </div>
      </div>
    </div>
  )
}

const PreviewLabel: React.FC<{ children: string }> = (props) => {
  return (
    <div
      css={css`
        background-color: ${colors.cyan60};
        color: ${colors.white00};
        padding: 0 10px;
        line-height: 1.5;
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        ${mq.small} {
          font-size: 13px;
        }
      `}
    >
      {props.children}
    </div>
  )
}

const isArticle = (content: any): content is Article =>
  content.newTags !== undefined
